import { useState, useMemo } from "react";
import { PackageCard } from "../components/PackageCard";
import { PageHero } from "../components/sections";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { CTABand } from "../components/sections";
import { usePackages } from "../hooks/usePackages";
import { IMAGES } from "../data/site";
import { Loader2 } from "lucide-react";

const BUDGETS = [
  { label: "All Budgets", min: 0, max: Infinity },
  { label: "Under ₹50,000", min: 0, max: 50000 },
  { label: "₹50k – ₹1L", min: 50000, max: 100000 },
  { label: "Above ₹1L", min: 100000, max: Infinity },
];

export const ToursListing = ({ type, eyebrow, title, subtitle, image }) => {
  const { data: all = [], isLoading } = usePackages(type);
  const regions = ["All Regions", ...Array.from(new Set(all.map((p) => p.region)))];
  const [region, setRegion] = useState("All Regions");
  const [budget, setBudget] = useState(0);

  const filtered = useMemo(() => {
    const b = BUDGETS[budget];
    return all.filter(
      (p) => (region === "All Regions" || p.region === region) && p.price >= b.min && p.price < b.max
    );
  }, [region, budget, all]);

  return (
    <div>
      <div className="pt-0"><PageHero eyebrow={eyebrow} title={title} subtitle={subtitle} image={image} /></div>

      <section className="ll-container py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Filters */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <h3 className="font-display text-2xl font-bold mb-6">Filter</h3>
              <div className="mb-8">
                <p className="eyebrow text-[#4A5D58] mb-4">Region</p>
                <div className="flex flex-wrap lg:flex-col gap-2">
                  {regions.map((r) => (
                    <button key={r} data-testid={`filter-region-${r.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setRegion(r)} className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${region === r ? "bg-[#0A221C] text-white" : "text-[#4A5D58] hover:bg-black/5"}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="eyebrow text-[#4A5D58] mb-4">Budget</p>
                <div className="flex flex-wrap lg:flex-col gap-2">
                  {BUDGETS.map((b, i) => (
                    <button key={b.label} data-testid={`filter-budget-${i}`} onClick={() => setBudget(i)} className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${budget === i ? "bg-[#4ef72d] text-[#0A221C]" : "text-[#4A5D58] hover:bg-black/5"}`}>
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-3">
            <p className="text-[#4A5D58] mb-8" data-testid="results-count">{filtered.length} {filtered.length === 1 ? "tour" : "tours"} found</p>
            {isLoading ? (
              <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={32} /></div>
            ) : filtered.length ? (
              <Stagger className="grid sm:grid-cols-2 gap-8">
                {filtered.map((p) => <StaggerItem key={p.slug}><PackageCard pkg={p} /></StaggerItem>)}
              </Stagger>
            ) : (
              <Reveal><p className="text-[#4A5D58] text-lg py-20 text-center">No tours match these filters. Try widening your budget.</p></Reveal>
            )}
          </div>
        </div>
      </section>

      <div className="pb-24"><CTABand /></div>
    </div>
  );
};

export const DEFAULT_IMAGES = IMAGES;
